import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { FilterService } from '@wilson/filter';
import { TimelineDateQueryParamKey, ZOOM_LEVEL } from '@wilson/interfaces';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  TimelineSetCurrentDate,
  UpdateWindowWidthHeightToWindowInnerWidthHeight,
} from '@wilson/shift-timeline/state';
import { DateTimeFormat } from '@wilson/utils';
import { add, addDays, format, startOfMonth, startOfWeek, sub } from 'date-fns';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { filter, Subscription } from 'rxjs';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'wilson-shift-timeline-date-picker',
  templateUrl: './shift-timeline-date-picker.component.html',
  styleUrls: ['./shift-timeline-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShiftTimelineDatePickerComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly filterService = inject(FilterService);
  private readonly route = inject(ActivatedRoute);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly cdRef = inject(ChangeDetectorRef);

  @Input() isViewOptionCardOpen = false;

  numberOfDaysToLoad!: number;

  @ViewChild(NzDatePickerComponent, { static: false })
  datePicker!: NzDatePickerComponent;

  subscription = new Subscription();
  protected hoveredDate: Date | null = null;
  protected zoomLevel$ = this.store.select(
    ShiftTimelineSettingsState.zoomLevel,
  );

  readonly faChevronLeft = faChevronLeft;
  readonly faChevronRight = faChevronRight;

  public selectedDate!: Date;
  private dateRange!: Date[];

  ngOnInit(): void {
    this.subscription.add(
      this.store
        .select(ShiftTimelineSettingsState.zoomLevel)
        .subscribe(async (zoomLevel: ZOOM_LEVEL) => {
          this.numberOfDaysToLoad =
            this.timelineCommonOperationsService.calculateNumberOfDaysToLoad();

          const timelineDate: string =
            this.route.snapshot.queryParams[TimelineDateQueryParamKey];

          let startDate = new Date();
          if (
            zoomLevel === ZOOM_LEVEL.DEFAULT ||
            zoomLevel === ZOOM_LEVEL.HALF_MONTH
          ) {
            startDate = startOfWeek(new Date(), { weekStartsOn: 1 });
          } else if (zoomLevel === ZOOM_LEVEL.MONTH) {
            startDate = startOfMonth(new Date());
          }

          if (timelineDate && this.isValidDate(timelineDate)) {
            startDate = new Date(timelineDate);
          }
          const endDate = addDays(startDate, this.numberOfDaysToLoad);
          this.dateRange = [startDate, endDate];
          this.selectedDate = this.dateRange[0];

          this.timelineCommonOperationsService.setTimelineDate(
            startDate,
            endDate,
          );
          this.store.dispatch(
            new UpdateWindowWidthHeightToWindowInnerWidthHeight(),
          ); // We update the window again to ensure the width is correct due to this rare bug https://menlo1679.atlassian.net/browse/WILP-7777
          this.cdRef.detectChanges();
        }),
    );

    this.subscription.add(
      this.store
        .select(ShiftTimelineDataState.currentDate)
        .pipe(filter(Boolean))
        .subscribe((currentDate) => {
          const timelineDate = currentDate;
          if (timelineDate && this.isValidDate(timelineDate)) {
            if (
              timelineDate !==
              format(this.selectedDate, DateTimeFormat.DatabaseDateFormat)
            ) {
              this.updateSelectedDate(new Date(timelineDate));
              this.cdRef.detectChanges();
            }
          } else {
            this.selectedDate = new Date();
          }
        }),
    );
  }

  updateSelectedDate(selectedDate: Date): void {
    const formattedDate = format(
      selectedDate,
      DateTimeFormat.DatabaseDateFormat,
    );
    this.store.dispatch(new TimelineSetCurrentDate(formattedDate));
    this.filterService.saveValueInQueryParam(
      TimelineDateQueryParamKey,
      formattedDate,
    );
    const endDate = add(new Date(selectedDate), {
      days: this.numberOfDaysToLoad,
    });

    this.dateRange = [selectedDate, endDate];
    this.selectedDate = this.dateRange[0];
  }

  onCalendarChange(selectedDate: Date | null): void {
    this.datePicker.close();

    if (selectedDate !== null) {
      this.updateSelectedDate(selectedDate);
      this.timelineCommonOperationsService.setTimelineDate(
        selectedDate,
        this.dateRange[1],
      );
    }
  }

  previousDate(): void {
    let startDate: Date;
    let endDate: Date;

    const date = this.store.selectSnapshot(ShiftTimelineDataState.currentDate);

    const numberOfDays = this.store.selectSnapshot(
      ShiftTimelineSettingsState.numberOfDaysFromZoomLevel,
    );

    if (date) {
      startDate = sub(new Date(date), { days: numberOfDays });
      endDate = add(new Date(startDate), {
        days: this.numberOfDaysToLoad,
      });
    } else {
      startDate = sub(this.dateRange[0], {
        days: 1,
      });
      endDate = sub(this.dateRange[1], {
        days: 1,
      });
    }

    const formattedDate = format(startDate, DateTimeFormat.DatabaseDateFormat);
    this.store.dispatch(new TimelineSetCurrentDate(formattedDate));
    this.filterService.saveValueInQueryParam(
      TimelineDateQueryParamKey,
      formattedDate,
    );

    this.dateRange = [startDate, endDate];
    this.selectedDate = this.dateRange[0];

    this.timelineCommonOperationsService.setTimelineDate(startDate, endDate);
  }

  nextDate(): void {
    let startDate: Date;
    let endDate: Date;
    const numberOfDays = this.store.selectSnapshot(
      ShiftTimelineSettingsState.numberOfDaysFromZoomLevel,
    );
    const date = this.store.selectSnapshot(ShiftTimelineDataState.currentDate);
    if (date) {
      startDate = add(new Date(date), { days: numberOfDays });
      endDate = add(new Date(startDate), {
        days: this.numberOfDaysToLoad,
      });
    } else {
      startDate = add(new Date(this.dateRange[0]), {
        days: 1,
      });
      endDate = add(this.dateRange[1], {
        days: 1,
      });
    }

    const formattedDate = format(startDate, DateTimeFormat.DatabaseDateFormat);
    this.store.dispatch(new TimelineSetCurrentDate(formattedDate));
    this.filterService.saveValueInQueryParam(
      TimelineDateQueryParamKey,
      formattedDate,
    );

    this.dateRange = [startDate, endDate];
    this.selectedDate = this.dateRange[0];

    this.timelineCommonOperationsService.setTimelineDate(startDate, endDate);
  }

  isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  setHoverDate(date: Date | null): void {
    this.hoveredDate = date;
  }

  ngOnDestroy(): void {
    this.store.dispatch(new TimelineSetCurrentDate(null));
    this.subscription.unsubscribe();
  }
}
